import { useState } from 'react';
import Icon from './Icon';
import FilterSheet from './FilterSheet';

// The single page-level export control for ניתוח רכש.
//
// Exports used to sit on each card — supplier, classification — plus two more inside
// the drill-downs, so one screen carried five of them and none said what the
// others did. This is the one control for everything the page can export
// WITHOUT a selection. The two drill-down exports stay where they are: they
// carry a supplier or a product the header has no way to name.
//
// It is a LABELLED button, not a second glyph next to the ⋮. Two icon-only
// buttons side by side are a puzzle; a word beside a glyph is not. The colour
// carries the scope: green btn-excel = whole page, outline = one row.
//
// Not a modal — there is nothing to configure, just three mutually exclusive
// picks. Not folded into the ⋮ either: that menu holds rare page actions
// (רענן, דוח שינויי מחיר) and export is the most-used action on the screen.

export default function ExportMenu({ options, scopeLabel }) {
  const [open, setOpen] = useState(false);
  const [busyKey, setBusyKey] = useState(null);
  const [error, setError] = useState('');
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 720;

  const run = async (opt) => {
    setBusyKey(opt.key);
    setError('');
    try {
      await opt.run();
      setOpen(false);
    } catch (err) {
      // The server refuses an over-large export rather than truncating it, so
      // this message is load-bearing — swallowing it would leave the user
      // staring at a button that did nothing.
      setError(err?.message || 'שגיאה בייצוא');
    } finally {
      setBusyKey(null);
    }
  };

  const rows = (
    <div>
      {scopeLabel && (
        <div style={{ fontSize: 12, color: 'var(--ink-muted)', padding: '2px 4px 10px', borderBottom: '1px solid #eef2f7', marginBottom: 6 }}>
          הייצוא חל על התקופה והסינון שנבחרו · {scopeLabel}
        </div>
      )}
      {error && (
        <div className="badge-red" style={{ display: 'block', margin: '0 0 8px', padding: '8px 10px', borderRadius: 8, fontSize: 12.5 }}>
          {error}
        </div>
      )}
      {options.map(o => (
        <button
          key={o.key}
          className="page-menu-item"
          style={{ alignItems: 'flex-start', minHeight: 44 }}
          disabled={busyKey != null}
          onClick={() => run(o)}
        >
          <Icon name="spreadsheet" size={15} />
          <span style={{ display: 'block', textAlign: 'start' }}>
            <span style={{ display: 'block', fontWeight: 600 }}>{o.label}</span>
            {/* An option whose label already says everything carries no hint —
                rendering the line anyway left a blank row under it. The busy
                state still needs the line, so it survives without one. */}
            {(busyKey === o.key || o.hint) && (
              <span style={{ display: 'block', fontSize: 11.5, color: '#94a3b8' }}>
                {busyKey === o.key ? 'מכין קובץ…' : o.hint}
              </span>
            )}
          </span>
        </button>
      ))}
    </div>
  );

  return (
    <span className="page-menu" style={{ position: 'relative' }}>
      <button
        className="btn btn-sm btn-excel"
        aria-expanded={open}
        onClick={() => { setError(''); setOpen(o => !o); }}
      >
        <Icon name="spreadsheet" size={15} /> דוחות
      </button>

      {open && isMobile && (
        <FilterSheet open title="ייצוא לאקסל" onClose={() => setOpen(false)}>
          {rows}
        </FilterSheet>
      )}

      {open && !isMobile && (
        <>
          <div className="menu-backdrop" onClick={() => setOpen(false)} />
          <div className="page-menu-panel" style={{ minWidth: 300 }}>{rows}</div>
        </>
      )}
    </span>
  );
}
